// This should be in a separate file, e.g., src/Contexts/UnitContext.jsx

import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import APICall from "../Utility/APICall";
import { paths } from "../Utility/Constants";
import { useAuth } from "../Utility/AuthProvider";

// Context for ALL units
export const UnitContext = createContext();

// Provider for ALL units
export const UnitProvider = ({ children }) => {
  const { jwt } = useAuth();
  const [allUnits, setAllUnits] = useState([]);
  const [isLoadingAllUnits, setIsLoadingAllUnits] = useState(false);
  const [allUnitsError, setAllUnitsError] = useState(null);

  // States for counts derived from all units
  const [totalUnitsCount, setTotalUnitsCount] = useState(0);
  const [rentedUnitsCount, setRentedUnitsCount] = useState(0);
  const [vacantUnitsCount, setVacantUnitsCount] = useState(0);

  // Cache for the list of all units
  const lastAllUnitsFetchTime = useRef(0);
  const CACHE_LIFETIME = 5 * 60 * 1000; // 5 minutes

  // This is the function that makes the unit.fetch API call
  const fetchAllUnits = useCallback(
    async (forceRefresh = false) => {
      if (
        !forceRefresh &&
        Date.now() - lastAllUnitsFetchTime.current < CACHE_LIFETIME &&
        allUnits.length > 0 // Ensure we have data before serving from cache
      ) {
        console.log("Serving all units from cache (UnitContext).");
        return;
      }

      if (!jwt) {
        setAllUnitsError("Authentication token not available.");
        setIsLoadingAllUnits(false);
        return;
      }

      setIsLoadingAllUnits(true);
      setAllUnitsError(null);
      try {
        const response = await APICall(
          "GET",
          paths.unit.fetch, // <-- HERE IT IS! The unit.fetch call
          undefined,
          undefined,
          jwt
        );

        if (response.status === "success" && Array.isArray(response.response)) {
          const fetchedUnits = response.response;
          setAllUnits(fetchedUnits);
          lastAllUnitsFetchTime.current = Date.now();

          // Calculate and set counts after fetching all units
          setTotalUnitsCount(fetchedUnits.length);
          const vacantCount = fetchedUnits.filter(
            (unit) => unit.occupancy_status === "vacant"
          ).length;
          setVacantUnitsCount(vacantCount);
          setRentedUnitsCount(fetchedUnits.length - vacantCount);
        } else {
          setAllUnitsError(response.message || "Failed to fetch all units.");
          setAllUnits([]);
          setTotalUnitsCount(0);
          setVacantUnitsCount(0);
          setRentedUnitsCount(0);
        }
      } catch (error) {
        setAllUnitsError(
          "An unexpected error occurred while fetching all units."
        );
        setAllUnits([]);
        setTotalUnitsCount(0);
        setVacantUnitsCount(0);
        setRentedUnitsCount(0);
      } finally {
        setIsLoadingAllUnits(false);
      }
    },
    [jwt]
    //removed 'allUnits' from dependencies to avoid unnecessary re-fetching
  );

  const resetUnitData = useCallback(() => {
    setAllUnits([]);
    setAllUnitsError(null);
    setTotalUnitsCount(0);
    setRentedUnitsCount(0);
    setVacantUnitsCount(0);
    console.log("UnitContext: State has been reset due to logout.");
  }, []);

  // Initial fetch when the provider mounts
  useEffect(() => {
    if (jwt) {
      console.log("UnitProvider: JWT found, fetching units.");
      fetchAllUnits();
    } else {
      resetUnitData();
    }
  }, [jwt, fetchAllUnits, resetUnitData]);

  return (
    <UnitContext.Provider
      value={{
        allUnits,
        isLoadingAllUnits,
        allUnitsError,
        totalUnitsCount,
        rentedUnitsCount,
        vacantUnitsCount,
        fetchAllUnits,
        resetUnitData,
      }}
    >
      {children}
    </UnitContext.Provider>
  );
};

// Hook to consume all units data
export const useUnits = () => {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error("useUnits must be used within a UnitProvider");
  }
  return context;
};
